import type {EditionSummary, SummaryArticle} from '../../types/types.ts';
import {isAutoSummaryEnabled} from '../lib/i18n.ts';
import {generateSummary, loadEditionSummary} from '../services/content.ts';
import type {Article} from './article.ts';
import type {ReaderEdition, ReaderItem} from './reader-edition.ts';
import {createSummaryArticle} from './summary-article.ts';

/**
 * Loads summary for an edition and injects it as the first article.
 * Handles both DigitalEdition (server-side summary) and ContentSourceEdition (client-side generation).
 */
export async function injectSummaryArticle(
	edition: ReaderEdition,
	articles: ReaderItem[],
	isContentSource: boolean
): Promise<ReaderItem[]> {
	if (!isAutoSummaryEnabled()) {
		return articles;
	}

	try {
		let summary: EditionSummary;

		if (isContentSource) {
			// ContentSourceEdition: Generate summary from articles
			// Include publishDate in editionId to ensure unique cache per date
			const editionIdWithDate = `${edition.id}:${edition.publishDate}`;
			const summaryArticles: SummaryArticle[] = articles.map((article) => ({
				id: article.id,
				title: article.title,
				leadText: (article as Article).intro || '',
				section: article.section || ''
			}));
			summary = await generateSummary(editionIdWithDate, summaryArticles);
		} else {
			// DigitalEdition/PrintEdition: Load pre-generated summary
			summary = await loadEditionSummary(edition.id);
		}

		if (summary.content && !summary.pending && !summary.error) {
			const summaryArticle = createSummaryArticle(summary, edition.id, edition.publishDate);
			return [summaryArticle, ...articles];
		}
	} catch (error) {
		console.error('Failed to load/generate summary:', error);
	}

	return articles;
}
